import {
  model,
  Model,
  prop,
  _async,
  _await,
  idProp,
  modelAction,
  modelFlow,
  Frozen,
  frozen,
  rootRef,
  detach,
  findParent,
  Ref,
  getSnapshot,
} from "mobx-keystone";
import { computed, observable } from "mobx";
import { createContext } from "react";
import { has, last } from "lodash";
import { notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { BobsWorkspace } from "./BobsWorkspace";
import {
  BOB_MODALS,
  BOB_PAGES,
  BobModalType,
  BobPageType,
} from "../helpers/constants";
import {
  sendDataAndDirectiveToBobController,
  sendToBobController,
  sendToRailsController,
} from "../../helpers/requests";
import {
  BobPayattemptFromServer,
  PayAttempt,
  PayAttemptRef,
  PayAttemptStatus,
  STATUSES_SHOWING_SERVER_PERSISTENCE,
} from "./PayAttempt";
import { decode } from "@gandlaf21/bolt11-decode";
import {
  handleFirstPayattemptFromServer,
  summariseBolt11Invoice,
} from "../helpers/payAttemptHelper";
import {
  BobNostrConnection,
  bobNostrConnectionRef,
} from "./BobNostrConnection";
import { CurrencySnapshot, EnvironmentData } from "../helpers/types";
import {
  makeSpreadsheetLineForOurInvoice,
  OurInvoice,
} from "../helpers/invoices";
import {
  makeSpreadsheetLineForPlatformCredit,
  PlatformCredit,
} from "../helpers/platformCredits";
import { makeSpreadsheetLines } from "../helpers/spreadSheet";
import { formatFiat } from "../helpers/formatNumbers";
import {
  makeTransactionListsLines,
  TransactionFilterType,
} from "../helpers/transactionsList";
import {
  getTransactionData,
  SelectedTransactionToView,
} from "../helpers/transactionViewer";
import { BobEvent } from "./BobEvent";
import { OurOnchainAddress } from "../helpers/onchainAddresses";
import { OurOnchainInboundTransaction } from "../helpers/onchainInboundTransactions";
import { OurOnchainOutboundTransaction } from "../helpers/onchainOutboundTransactions";
import { OurStablePayment } from "../helpers/stablePayments";
import {
  OnchainPaymentProposal,
  OnchainPaymentProposalRef,
} from "./OnchainPaymentProposal";
import {
  SolanaUsdtProposal,
  SolanaUsdtProposalRef,
} from "./SolanaUsdtProposal";
import {
  BobSponsorshipProposal,
  BobSponsorshipProposalRef,
} from "./BobSponsorshipProposal";
import { Sponsorship, SponsorshipRef } from "./Sponsorship";
import {
  HoldInvoice,
  holdAmountSatoshis,
  isUndecidedHoldInvoice,
} from "../helpers/holdInvoices";

export interface BobGiftFromServer {
  serial_code: string;
  secret_code: string;
  satoshis_amount: number;
  redemption_status: string;
  image_url_lossless: string | null;
  image_url_compressed: string | null;
  purchaser_private_note: string | null;
  purchased_at: number | null;
  redeemed_at: number | null;
  platform_credit_uuid: string | null;
  purchase_platform_credit_uuid: string | null;
  redemption_platform_credit_uuid: string | null;
}

export type BobGiftCardView = "shop" | "purchased";

// directives whose change only shows up after a page reload: instead of a
// "Success" toast AND a reload dialog, the reload dialog alone says what
// was saved (components/modals/MustRefreshPage.tsx)
const RELOAD_AFTER_DIRECTIVE: Record<string, string> = {
  "set-history-days": "Your transaction history setting was saved.",
  "change-two-factor-timeout": "Your 2FA timeout was saved.",
  "make-a-new-bob": "Your new vault was created.",
  "destroy-wallet-now": "Your Rizful vault was deleted.",
  "reset-user-browser-notifications": "Browser notifications were reset.",
};

@model("exrails/Bob")
export class Bob extends Model({
  _version: prop(0),
  id: idProp,
  uuid: prop<string>(""),
  alias: prop<string>(""),
  userFacingName: prop<string>(""),
  userProvidedNostrPublicKey: prop<string>(""),
  userProposedAlias: prop<string>(""),
  currencyCode: prop<string>("usd"),
  safeBalanceInSatoshis: prop<number>(0),
  pendingOnchainBalanceInSatoshis: prop<number>(0),
  showHistoryDays: prop<number>(30),
  inFlightSatoshis: prop<number>(0),
  openPage: prop<BobPageType>(BOB_PAGES.HOME),
  openModal: prop<BobModalType | null>(null),
  ourOnchainAddresses: prop<Frozen<OurOnchainAddress[]>>(() =>
    frozen<OurOnchainAddress[]>([]),
  ),
  ourOnchainInboundTransactions: prop<Frozen<OurOnchainInboundTransaction[]>>(
    () => frozen<OurOnchainInboundTransaction[]>([]),
  ),
  ourOnchainOutboundTransactions: prop<Frozen<OurOnchainOutboundTransaction[]>>(
    () => frozen<OurOnchainOutboundTransaction[]>([]),
  ),
  ourStablePayments: prop<Frozen<OurStablePayment[]>>(() =>
    frozen<OurStablePayment[]>([]),
  ),
  ourInvoices: prop<Frozen<OurInvoice[]>>(() => frozen<OurInvoice[]>([])),
  platformCredits: prop<Frozen<PlatformCredit[]>>(() =>
    frozen<PlatformCredit[]>([]),
  ),
  bobGifts: prop<Frozen<BobGiftFromServer[]>>(() =>
    frozen<BobGiftFromServer[]>([]),
  ),
  giftCardView: prop<BobGiftCardView>("shop"),
  // pay attempts
  userCreatedInvoicePayRequest: prop<string | null>(null),
  selectedPayAttemptRef: prop<Ref<PayAttempt> | undefined>(undefined),
  payAttempts: prop<PayAttempt[]>(() => []),
  nostrConnections: prop<BobNostrConnection[]>(() => []),
  selectedNostrConnectionRef: prop<Ref<BobNostrConnection> | undefined>(
    undefined,
  ),
  nostrConnectionsAreRefreshing: prop<boolean>(false),
  areWeWaitingOnRails: prop<boolean>(false),
  selectedTransactionToView: prop<Frozen<SelectedTransactionToView> | null>(
    null,
  ),
  userProposedNostrPublicKey: prop<string>(""),
  showSettledOurInvoicesOnly: prop<boolean>(true),
  transactionFilterType: prop<string>("all"),
  // onchain payment proposals
  selectedOnchainPaymentProposalRef: prop<
    Ref<OnchainPaymentProposal> | undefined
  >(undefined),
  onchainPaymentProposals: prop<OnchainPaymentProposal[]>(() => []),
  // solana usdt payment proposals
  selectedSolanaUsdtProposalRef: prop<Ref<SolanaUsdtProposal> | undefined>(
    undefined,
  ),
  solanaUsdtProposals: prop<SolanaUsdtProposal[]>(() => []),
  // sponsorship proposals
  selectedBobSponsorshipProposalRef: prop<
    Ref<BobSponsorshipProposal> | undefined
  >(undefined),
  bobSponsorshipProposals: prop<BobSponsorshipProposal[]>(() => []),
  // sponsorships from server
  selectedSponsorshipRef: prop<Ref<Sponsorship> | undefined>(undefined),
  sponsorships: prop<Sponsorship[]>(() => []),
  // hold invoices of THIS vault (zz-support-hold-invoices-fable.txt 10)
  holdInvoices: prop<Frozen<HoldInvoice[]>>(() => frozen<HoldInvoice[]>([])),
  holdInvoicesLoaded: prop<boolean>(false),
  holdInvoiceBusyUuid: prop<string | null>(null),
  holdInvoiceErrors: prop<Frozen<Record<string, string>>>(() =>
    frozen<Record<string, string>>({}),
  ),
  decisionModalHoldUuid: prop<string | null>(null),
  celebratedHoldUuid: prop<string | null>(null),
  // what was just saved, shown by the reload dialog
  mustRefreshMessage: prop<string | null>(null),
  createdHoldInvoicePayRequest: prop<string | null>(null),
  holdCreateError: prop<string | null>(null),
}) {
  // Replaces the snapshot and diffs it like setOurInvoices: a hold that just
  // became accepted pops the decision modal and gets an events-drawer entry.
  // Never optimistic: this is always what Rails last learned from lnd.
  @modelAction
  setHoldInvoices = (data: HoldInvoice[]) => {
    const previous = new Map(
      this.holdInvoices.data.map((hold) => [hold.uuid, hold]),
    );
    const newlyAccepted: HoldInvoice[] = [];
    (data || []).forEach((hold) => {
      const before = previous.get(hold.uuid);
      if (
        this.holdInvoicesLoaded &&
        isUndecidedHoldInvoice(hold) &&
        (!before || before.hold_state !== "accepted")
      ) {
        newlyAccepted.push(hold);
      }
      if (
        this.holdInvoicesLoaded &&
        before &&
        before.hold_state !== "settled" &&
        hold.hold_state === "settled"
      ) {
        this.workspace.makeEvent(
          "Payment Received",
          `A hold invoice payment of ${holdAmountSatoshis(hold).toLocaleString()} satoshis was accepted.`,
          "invoice-paid",
          hold.pay_request,
          hold.uuid,
        );
      }
    });
    this.holdInvoices = frozen([...(data || [])]);
    this.holdInvoicesLoaded = true;

    newlyAccepted.forEach((hold) => {
      this.workspace.makeEvent(
        "Payment waiting for your decision",
        `A payment of ${holdAmountSatoshis(hold).toLocaleString()} satoshis arrived on a hold invoice. Accept or reject it.`,
        "hold-invoice-accepted",
        null,
        hold.uuid,
      );
    });
    if (newlyAccepted.length > 0 && this.workspace.selectedBob === this) {
      this.openHoldDecisionModal(this.undecidedHoldInvoices[0].uuid);
    }
  };

  // opens the aggressive decision modal, unless another modal is in use (the
  // badge and banner keep nagging, and the 10-minute re-nag retries)
  @modelAction
  openHoldDecisionModal = (uuid: string, force = false) => {
    if (
      !force &&
      this.openModal &&
      this.openModal !== BOB_MODALS.HOLD_INVOICE_NEEDS_DECISION
    ) {
      return;
    }
    this.decisionModalHoldUuid = uuid;
    this.workspace.markHoldModalInteraction();
    this.setOpenModal(BOB_MODALS.HOLD_INVOICE_NEEDS_DECISION);
  };

  @modelFlow
  setSendEmailForHoldInvoice = _async(function* (this: Bob, sendEmail: boolean) {
    const response = yield* _await(
      sendDataAndDirectiveToBobController(
        this.uuid,
        "set-send-email-for-hold-invoice",
        { sendEmail },
        "/bob_life/directive_and_data",
      ),
    );
    if (response?.success === true) {
      this.workspace.setAccountSendEmailForHoldInvoice(
        response.sendEmailForHoldInvoice,
      );
    } else {
      notifications.show({
        title: "Error",
        message: response?.message || "Could not save the setting.",
        color: "red",
      });
    }
  });

  @modelAction
  setHoldInvoiceBusyUuid = (uuid: string | null) => {
    this.holdInvoiceBusyUuid = uuid;
  };

  @modelAction
  setHoldInvoiceError = (uuid: string, message: string | null) => {
    const next = { ...this.holdInvoiceErrors.data };
    if (message) next[uuid] = message;
    else delete next[uuid];
    this.holdInvoiceErrors = frozen(next);
  };

  @modelAction
  setCreatedHoldInvoicePayRequest = (payRequest: string | null) => {
    this.createdHoldInvoicePayRequest = payRequest;
  };

  @modelAction
  setHoldCreateError = (message: string | null) => {
    this.holdCreateError = message;
  };

  @modelAction
  setCelebratedHoldUuid = (uuid: string | null) => {
    this.celebratedHoldUuid = uuid;
  };

  @computed
  get acceptedHoldInvoices(): HoldInvoice[] {
    return this.holdInvoices.data
      .filter((hold) => hold.hold_state === "accepted")
      .sort((a, b) => (a.hold_accepted_at ?? 0) - (b.hold_accepted_at ?? 0));
  }

  @computed
  get undecidedHoldInvoices(): HoldInvoice[] {
    return this.acceptedHoldInvoices.filter(isUndecidedHoldInvoice);
  }

  @computed
  get openHoldInvoices(): HoldInvoice[] {
    return this.holdInvoices.data.filter((hold) => hold.hold_state === "open");
  }

  @computed
  get recentHoldInvoices(): HoldInvoice[] {
    return this.holdInvoices.data.filter(
      (hold) =>
        hold.hold_state === "settled" ||
        hold.hold_state === "canceled" ||
        hold.hold_state === "expired",
    );
  }

  @computed
  get acceptedHoldCount() {
    return this.acceptedHoldInvoices.length;
  }

  // POST /bob_life/create_hold_invoice; the server's refusal (caps,
  // eligibility) is shown inside the modal, not just as a toast
  @modelFlow
  createHoldInvoice = _async(function* (
    this: Bob,
    amountInSatoshis: number,
    invoiceDescription: string,
    expirySeconds: number,
  ) {
    this.setAreWeWaitingOnRails(true);
    this.setHoldCreateError(null);
    try {
      const response = yield* _await(
        sendToBobController(
          {
            bobUuid: this.uuid,
            amountInSatoshis,
            invoiceDescription,
            expirySeconds,
          },
          "/bob_life/create_hold_invoice",
        ),
      );
      this.setAreWeWaitingOnRails(false);
      if (!response || response.success !== true) {
        this.setHoldCreateError(
          response?.message ||
            response?.reason ||
            "Could not create the invoice. Please try again.",
        );
        return false;
      }
      this.setHoldInvoices(response.hold_invoices || []);
      this.setCreatedHoldInvoicePayRequest(response.pay_request);
      this.workspace.makeEvent(
        "Hold Invoice Created",
        `A hold invoice in the amount of ${amountInSatoshis.toLocaleString()} satoshis has been created.`,
        "invoice-created",
        response.pay_request,
        response.uuid,
      );
      return true;
    } catch (error) {
      this.setAreWeWaitingOnRails(false);
      this.setHoldCreateError("Could not create the invoice. Please try again.");
      Sentry.captureException(error, {
        tags: { class: "Bob", function: "createHoldInvoice" },
        contexts: { hold: { bobUuid: this.uuid, amountInSatoshis } },
      });
      return false;
    }
  });

  // Accept / Reject. The result shown is lnd's, never optimistic (10.5):
  // success -> the card moves to Recent (Accept also celebrates, the ONE owner
  // of that confetti); pending -> the card shows Accepting…/Rejecting… from the
  // server flags until the ActionCable update; failure -> red inline alert.
  @modelFlow
  holdInvoiceAction = _async(function* (
    this: Bob,
    directive: "settle-hold-invoice" | "cancel-hold-invoice",
    uuid: string,
  ) {
    this.workspace.markHoldModalInteraction();
    this.setHoldInvoiceBusyUuid(uuid);
    this.setHoldInvoiceError(uuid, null);
    try {
      const response = yield* _await(
        sendDataAndDirectiveToBobController(
          this.uuid,
          directive,
          { holdInvoiceUuid: uuid },
          "/bob_life/directive_and_data",
        ),
      );
      if (response?.hold_invoices) this.setHoldInvoices(response.hold_invoices);
      const isSettle = directive === "settle-hold-invoice";
      if (response?.success === true) {
        if (this.openModal === BOB_MODALS.HOLD_INVOICE_NEEDS_DECISION) {
          this.setOpenModal(null);
        }
        if (isSettle) {
          this.setCelebratedHoldUuid(uuid);
          this.setOpenModal(BOB_MODALS.HOLD_INVOICE_WAS_ACCEPTED);
        } else {
          notifications.show({
            title: "Payment rejected",
            message: "The payer's funds are being returned.",
            color: "gray",
          });
        }
      } else if (response?.pending) {
        notifications.show({
          title: isSettle ? "Accepting…" : "Rejecting…",
          message:
            response.message ||
            "Still processing. This screen will update automatically.",
          color: "yellow",
        });
      } else {
        this.setHoldInvoiceError(
          uuid,
          response?.message || response?.reason || "Something went wrong.",
        );
      }
    } catch (error) {
      this.setHoldInvoiceError(
        uuid,
        "Could not reach Rizful. This screen will update when the payment changes.",
      );
      Sentry.captureException(error, {
        tags: { class: "Bob", function: "holdInvoiceAction" },
        contexts: { hold: { bobUuid: this.uuid, directive, uuid } },
      });
    } finally {
      this.setHoldInvoiceBusyUuid(null);
    }
  });

  @modelAction
  pushInNewOnchainAddress = (address: OurOnchainAddress) => {
    console.log("pushing in new onchain address", address);
    const currentAddresses = this.ourOnchainAddresses.data.slice();
    currentAddresses.unshift(address);
    this.ourOnchainAddresses = frozen(currentAddresses);
  };

  @modelAction
  setShowSettledOurInvoicesOnly = (value: boolean) => {
    console.log("setting showSettledOurInvoicesOnly to", value);
    this.showSettledOurInvoicesOnly = value;
  };

  @modelAction
  setTransactionFilterType = (filterType: TransactionFilterType) => {
    console.log("setting transactionFilterType to", filterType);
    this.transactionFilterType = filterType;
  };

  @modelAction
  checkIfWeNeedToShowTwoFactorForm() {
    if (this.workspace.accountIsTwoFactorProtected) {
      this.sendDataAndDirectiveToRails(
        "do-we-need-to-show-the-two-factor-form",
        {},
      );
    }
  }

  @modelAction
  setUserProposedNostrPublicKey = (key: string) => {
    console.log("setting user proposed nostr public key to", key);
    this.userProposedNostrPublicKey = key;
  };

  @modelAction
  setSelectedTransactionToView = (
    transactionToView: SelectedTransactionToView | null,
  ) => {
    if (transactionToView) {
      this.selectedTransactionToView = frozen(transactionToView);
      this.setOpenModal(BOB_MODALS.VIEW_TRANSACTION);
    } else {
      this.setOpenModal(null);
      this.selectedTransactionToView = null;
    }
  };

  @modelAction
  setAreWeWaitingOnRails = (value: boolean) => {
    //console.log("setting areWeWaitingOnRails to", value);
    this.areWeWaitingOnRails = value;
  };
  @modelAction
  handleDirectiveAndDataResponse(directive: string, response: any) {
    // console.log(
    //   "handling directive and data response from rails",
    //   directive,
    //   response
    // );
    if (response?.success === true) {
      const reloadMessage = RELOAD_AFTER_DIRECTIVE[directive];
      if (reloadMessage) {
        // one dialog, no toast: see RELOAD_AFTER_DIRECTIVE
        this.mustRefreshMessage = reloadMessage;
        this.setOpenModal(BOB_MODALS.YOU_MUST_REFRESH_THE_PAGE);
        return;
      }

      if (response?.message) {
        notifications.show({
          title: "Success",
          message: response.message,
          color: "green",
        });
      }

      // Handle specific directive responses
      if (directive === "set-user-provided-nostr-public-key") {
        this.userProvidedNostrPublicKey =
          this.userProposedNostrPublicKey.trim();
      }

      if (directive === "remove-user-provided-nostr-public-key") {
        this.userProvidedNostrPublicKey = "";
      }

      if (directive === "try-to-modify-alias") {
        // Update the actual alias with the proposed value and close modal
        this.alias = this.userProposedAlias.trim();
      }
      if (directive == "set-currency-code") {
        this.currencyCode = response.currencyCode;
      }
      if (directive == "do-we-need-to-show-the-two-factor-form") {
        console.log(
          // "setting workspace.accountRequiresTwoFactorFormForActions to",
          response.twoFactorIsRequired,
        );

        this.workspace.accountRequiresTwoFactorFormForActions =
          response.twoFactorIsRequired;
      }

      return;
    }
  }
  @modelFlow
  sendDataAndDirectiveToRails = _async(function* (
    this: Bob,
    directive: string,
    data: any,
  ) {
    // console.log("sending data and directive to rails", directive, data);
    this.setAreWeWaitingOnRails(true);
    sendDataAndDirectiveToBobController(
      this.uuid,
      directive,
      data,
      "/bob_life/directive_and_data",
    )
      .then((response) => {
        this.setAreWeWaitingOnRails(false);
        if (response.success == false) {
          notifications.show({
            title: "Error",
            message: response.message,
            color: "red",
          });
          return;
        }

        this.handleDirectiveAndDataResponse(directive, response);
      })
      .catch((error) => {
        this.areWeWaitingOnRails = false;
        console.log(error);
        Sentry.captureException(error, {
          tags: {
            class: "Bob",
            function: "sendDataAndDirectiveToRails",
            action: "request_failed",
          },
          contexts: {
            bob_details: {
              bobUuid: this.uuid,
              directive,
              data: JSON.stringify(data).substring(0, 500), // Limit data size
              areWeWaitingOnRails: this.areWeWaitingOnRails,
            },
          },
        });
      });
  });

  @modelAction
  setUserProvidedNostrPublicKey = (key: string) => {
    console.log("setting user provided nostr public key to", key);
    this.userProvidedNostrPublicKey = key;
  };

  @modelAction
  setUserProposedAlias = (alias: string) => {
    console.log("setting user proposed alias to", alias);
    this.userProposedAlias = alias;
  };

  @computed
  get sendAndReceiveNostrConnectionsOrderedByCreatedAtDesc() {
    return this.nostrConnections
      .slice()
      .filter((conn) => conn.kind === "send_and_receive")
      .sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });
  }

  @computed
  get receiveOnlyNostrConnection() {
    return this.nostrConnections.find((conn) => conn.kind === "receive_only");
  }

  @modelAction
  purgeNostrConnections() {
    console.log("purging nostr connections");
    this.selectedNostrConnectionRef = undefined;
    this.nostrConnections = [];
  }

  @modelAction
  refreshNostrConnections() {
    this.purgeNostrConnections();
    this.updateNostrConnections("refresh-all", null);
  }

  @modelFlow
  updateNostrConnections = _async(function* (
    this: Bob,
    directive,
    bobNostrConnectionUuid: string | null,
    twoFactorCode?: string | null,
  ) {
    this.nostrConnectionsAreRefreshing = true;
    const message = {
      bobUuid: this.uuid,
      directive: directive,
      bobNostrConnectionUuid: bobNostrConnectionUuid,
      twoFactorCode: twoFactorCode,
    };
    console.log("updating nostr connections");
    try {
      const response = yield* _await(
        sendToBobController(
          message,
          "/bob_nostr_connection/update_nostr_connections",
        ),
      );
      if (response.success == false) {
        this.nostrConnectionsAreRefreshing = false;
        notifications.show({
          title: "Error",
          message: response.message,
          color: "red",
        });
        return false;
      }
      this.instantiateNostrConnections(response.nostrConnections);
      return true;
    } catch (error) {
      this.nostrConnectionsAreRefreshing = false;
      console.log(error);
      Sentry.captureException(error, {
        tags: {
          class: "Bob",
          function: "updateNostrConnections",
          action: "nostr_connections_update",
        },
        contexts: {
          bob_details: {
            bobUuid: this.uuid,
            bobNostrConnectionUuid,
            twoFactorCode: twoFactorCode ? "provided" : "not_provided",
            nostrConnectionsAreRefreshing: this.nostrConnectionsAreRefreshing,
          },
        },
      });
      return false;
    }
  });

  @modelFlow
  updateNostrConnection = _async(function* (this: Bob, data: any) {
    this.nostrConnectionsAreRefreshing = true;
    const message = {
      bobUuid: this.uuid,
      data: data,
    };
    console.log("updating nostr connection", message);
    sendToBobController(
      message,
      "/bob_nostr_connection/update_nostr_connection",
    )
      .then((response) => {
        if (response.success == false) {
          notifications.show({
            title: "Error",
            message: response.message,
            color: "red",
          });
          return;
        }
        this.instantiateNostrConnections(response.nostrConnections);
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error, {
          tags: {
            class: "Bob",
            function: "updateNostrConnection",
            action: "single_nostr_connection_update",
          },
          contexts: {
            bob_details: {
              bobUuid: this.uuid,
              data: JSON.stringify(data).substring(0, 500), // Limit data size
              nostrConnectionsAreRefreshing: this.nostrConnectionsAreRefreshing,
            },
          },
        });
      });
  });

  @modelAction
  instantiateNostrConnections(data: any[]) {
    console.log("instantiating nostr connections");
    this.nostrConnections = data.map((nostrConnectionData) => {
      return new BobNostrConnection({
        kind: nostrConnectionData.kind,
        uuid: nostrConnectionData.uuid,
        name: nostrConnectionData.name,
        created_at: nostrConnectionData.created_at,
        budget_amount_sats: nostrConnectionData.budget_amount_sats,
        total_amount_left_in_budget:
          nostrConnectionData.total_amount_left_in_budget,
        budget_renewal_period_days:
          nostrConnectionData.budget_renewal_period_days,
        connection_expiration_days:
          nostrConnectionData.connection_expiration_days,
        budget_last_renewed: nostrConnectionData.budget_last_renewed,
        connection_expiration_measured_from_date:
          nostrConnectionData.connection_expiration_measured_from_date,
        nwc_uri: nostrConnectionData.nwc_uri,
      });
    });
    this.nostrConnectionsAreRefreshing = false;
  }

  @computed
  get selectedNostrConnection() {
    return this.selectedNostrConnectionRef
      ? this.selectedNostrConnectionRef.current
      : undefined;
  }
  @modelAction
  selectNostrConnection(nostrConnection: BobNostrConnection | undefined) {
    if (nostrConnection && !this.nostrConnections.includes(nostrConnection))
      throw new Error("unknown nostrconnection");
    this.selectedNostrConnectionRef = nostrConnection
      ? bobNostrConnectionRef(nostrConnection)
      : undefined;
  }

  // @computed
  // get payAttemptsPersisted(): PayAttempt[] {
  //   return this.payAttempts.filter((pa) =>
  //     STATUSES_SHOWING_SERVER_PERSISTENCE.includes(pa.payAttemptStatus)
  //   );
  // }
  @modelAction
  activatePayAttempts() {
    const arr = this.payAttempts;
    this.payAttempts = arr.slice(); // Reassign the array to force reactivity
  }

  @computed
  get payAttemptsPersisted(): PayAttempt[] {
    return this.payAttempts.filter((pa) => {
      return STATUSES_SHOWING_SERVER_PERSISTENCE.includes(pa.payAttemptStatus);
    });
  }
  @modelAction
  refreshPayAttempt = () => {
    this.selectPayAttempt(undefined);
    this.makeNewPayAttemptIfNecessary();
  };

  @modelAction
  makeNewPayAttemptIfNecessary() {
    if (!this.selectedPayAttempt) {
      console.log("making a new blank pay attempt");
      const pa = new PayAttempt({});

      this.payAttempts.push(pa);
      this.selectPayAttempt(pa);
    }
  }

  @computed
  get selectedPayAttempt() {
    return this.selectedPayAttemptRef
      ? this.selectedPayAttemptRef.current
      : undefined;
  }
  @modelAction
  selectPayAttempt(payAttempt: PayAttempt | undefined) {
    if (payAttempt && !this.payAttempts.includes(payAttempt))
      throw new Error("unknown payAttempt");
    this.selectedPayAttemptRef = payAttempt
      ? PayAttemptRef(payAttempt)
      : undefined;
  }

  // OnchainPaymentProposal methods
  @computed
  get selectedOnchainPaymentProposal() {
    return this.selectedOnchainPaymentProposalRef
      ? this.selectedOnchainPaymentProposalRef.current
      : undefined;
  }

  @modelAction
  selectOnchainPaymentProposal(proposal: OnchainPaymentProposal | undefined) {
    if (proposal && !this.onchainPaymentProposals.includes(proposal))
      throw new Error("unknown onchainPaymentProposal");
    this.selectedOnchainPaymentProposalRef = proposal
      ? OnchainPaymentProposalRef(proposal)
      : undefined;
  }

  @modelAction
  deleteAllOnchainPaymentProposals() {
    this.selectedOnchainPaymentProposalRef = undefined;
    this.onchainPaymentProposals = [];
  }

  @modelAction
  createAndSelectNewOnchainPaymentProposal() {
    this.deleteAllOnchainPaymentProposals();
    const proposal = new OnchainPaymentProposal({});
    this.onchainPaymentProposals.push(proposal);
    this.selectOnchainPaymentProposal(proposal);
    return proposal;
  }

  // SolanaUsdtProposal methods
  @computed
  get selectedSolanaUsdtProposal() {
    return this.selectedSolanaUsdtProposalRef
      ? this.selectedSolanaUsdtProposalRef.current
      : undefined;
  }

  @modelAction
  selectSolanaUsdtProposal(proposal: SolanaUsdtProposal | undefined) {
    if (proposal && !this.solanaUsdtProposals.includes(proposal))
      throw new Error("unknown solanaUsdtProposal");
    this.selectedSolanaUsdtProposalRef = proposal
      ? SolanaUsdtProposalRef(proposal)
      : undefined;
  }

  @modelAction
  deleteAllSolanaUsdtProposals() {
    this.selectedSolanaUsdtProposalRef = undefined;
    this.solanaUsdtProposals = [];
  }

  @modelAction
  createAndSelectNewSolanaUsdtProposal() {
    this.deleteAllSolanaUsdtProposals();
    const proposal = new SolanaUsdtProposal({});
    this.solanaUsdtProposals.push(proposal);
    this.selectSolanaUsdtProposal(proposal);
    return proposal;
  }

  // BobSponsorshipProposal methods
  @computed
  get selectedBobSponsorshipProposal() {
    return this.selectedBobSponsorshipProposalRef
      ? this.selectedBobSponsorshipProposalRef.current
      : undefined;
  }

  @modelAction
  selectBobSponsorshipProposal(proposal: BobSponsorshipProposal | undefined) {
    if (proposal && !this.bobSponsorshipProposals.includes(proposal))
      throw new Error("unknown bobSponsorshipProposal");
    this.selectedBobSponsorshipProposalRef = proposal
      ? BobSponsorshipProposalRef(proposal)
      : undefined;
  }

  @modelAction
  deleteAllBobSponsorshipProposals() {
    this.selectedBobSponsorshipProposalRef = undefined;
    this.bobSponsorshipProposals = [];
  }

  @modelAction
  createAndSelectNewBobSponsorshipProposal() {
    this.deleteAllBobSponsorshipProposals();
    const proposal = new BobSponsorshipProposal({});
    this.bobSponsorshipProposals.push(proposal);
    this.selectBobSponsorshipProposal(proposal);
    return proposal;
  }

  // Sponsorship methods
  @computed
  get selectedSponsorship() {
    return this.selectedSponsorshipRef
      ? this.selectedSponsorshipRef.current
      : undefined;
  }

  @modelAction
  selectSponsorship(sponsorship: Sponsorship | undefined) {
    if (sponsorship && !this.sponsorships.includes(sponsorship))
      throw new Error("unknown sponsorship");
    this.selectedSponsorshipRef = sponsorship
      ? SponsorshipRef(sponsorship)
      : undefined;
  }

  @modelAction
  setSponsorships(data: any) {
    this.selectedSponsorshipRef = undefined;
    this.sponsorships = [];
    if (data) {
      const s = new Sponsorship({
        satoshisPerView: Number(data.satoshis_per_view) || 0,
        startingBonusSatoshis: data.starting_bonus_satoshis || 0,
        tokuserName: data.tokuser_name || "",
        maximumViews: data.maximum_views || 0,
        startingBonusRedeemedAt: data.starting_bonus_redeemed_at || null,
        isAllowedToScrape: data.is_allowed_to_scrape || false,
        nextScrapeAllowedAt: data.next_scrape_allowed_at || null,
        availableRedemptionSatoshis: data.available_redemption_satoshis || 0,
        hasReachedMaximumViews: data.has_reached_maximum_views || false,
        videos: frozen(data.videos || []),
        redemptions: frozen(data.redemptions || []),
      });
      this.sponsorships.push(s);
      this.selectSponsorship(s);
      console.log("set sponsorships to", getSnapshot(this.sponsorships));
    }
  }

  @modelAction
  setOurInvoices = (data: OurInvoice[]) => {
    /* index current invoices by payment_hash for O(1) look-ups */
    const previous = new Map(
      this.ourInvoices.data.map((inv) => [inv.payment_hash, inv]),
    );

    const interestingInvoices: OurInvoice[] = [];

    data.forEach((inv) => {
      // hold invoices: the Hold Invoices page owns their celebration (10.5)
      if (inv.is_hold_invoice) return;
      const before = previous.get(inv.payment_hash);

      // console.log("invoice created at ", inv.created_at);
      // console.log("current unix time is ", Date.now());
      // console.log("page was loaded at", this.workspace.pageLoadedAtInteger);

      if (
        !before &&
        inv.confirmed_at &&
        inv.confirmed_at > this.workspace.pageLoadedAtInteger
      ) {
        // console.log(
        //   "this is a confirmed invoice that the front end didnt know about, and appeared since page load"
        // );
        interestingInvoices.push(inv);
      }
      if (before && before.confirmed_at === null && inv.confirmed_at !== null) {
        // console.log(
        //   "this is an invoice the front end did know about but that was unconfirmed"
        // );
        interestingInvoices.push(inv);
      }
    });

    if (interestingInvoices.length) {
      this.handleNewlyPaidInvoices(interestingInvoices);
    }

    /* finally replace the frozen snapshot */
    this.ourInvoices = frozen([...data]);
  };
  @modelAction
  handleNewlyPaidInvoices = (invoices: OurInvoice[]) => {
    console.log("handling interesting invoices", invoices);
    invoices.forEach((invoice) => {
      this.workspace.makeEvent(
        "Payment Received",
        `A payment in the amount of ${invoice.amount_in_satoshis.toLocaleString()} satoshis has been received.`,
        "invoice-paid",
        invoice.pay_request,
        invoice.uuid,
      );
      if (
        invoice.pay_request == this.userCreatedInvoicePayRequest &&
        invoice.confirmed_at
      ) {
        console.log(
          "the invoice screen is still open for this so we should do something",
          invoice.pay_request,
        );
        this.setOpenModal(BOB_MODALS.OUR_INVOICE_WAS_PAID);
      }
    });
  };

  @modelAction
  setPlatformCredits = (data: any[]) => {
    //console.log("setting platform credits", data);
    this.platformCredits = frozen(data);
  };

  @modelAction
  setBobGifts = (data: BobGiftFromServer[]) => {
    this.bobGifts = frozen([...(data || [])]);
  };

  @modelAction
  setGiftCardView = (view: BobGiftCardView) => {
    this.giftCardView = view;
  };

  @modelAction
  setOurOnchainAddresses = (data: OurOnchainAddress[]) => {
    // console.log("setting onchain addresses", data);
    this.ourOnchainAddresses = frozen([...data]);
  };

  @modelAction
  setOurOnchainInboundTransactions = (data: OurOnchainInboundTransaction[]) => {
    // console.log("setting onchain inbound transactions", data);
    this.ourOnchainInboundTransactions = frozen([...data]);
  };

  @modelAction
  setOurOnchainOutboundTransactions = (
    data: OurOnchainOutboundTransaction[],
  ) => {
    // console.log("setting onchain outbound transactions", data);
    this.ourOnchainOutboundTransactions = frozen([...data]);
  };

  @modelAction
  setOurStablePayments = (data: OurStablePayment[]) => {
    // console.log("setting stable payments", data);
    this.ourStablePayments = frozen([...data]);
  };

  @modelAction
  updateOnchainInboundTransactions = (
    data: OurOnchainInboundTransaction[],
    pendingOnchainBalanceInSatoshis: number,
  ) => {
    console.log("updating onchain inbound transactions", data);

    // Check for new transactions and create events for them
    const existingTxIds = new Set(
      this.ourOnchainInboundTransactions.data.map(
        (tx) => tx.transaction_identifier,
      ),
    );

    data.forEach((tx) => {
      if (!existingTxIds.has(tx.transaction_identifier)) {
        this.workspace.makeEvent(
          "Onchain Payment Received",
          `An onchain payment of ${tx.satoshis_amount.toLocaleString()} satoshis has been received${
            tx.is_considered_confirmed
              ? " and confirmed"
              : " (pending confirmation)"
          }.`,
          "onchain-payment-received",
          null,
          tx.transaction_identifier,
        );
      }
    });

    this.ourOnchainInboundTransactions = frozen([...data]);
    this.pendingOnchainBalanceInSatoshis = pendingOnchainBalanceInSatoshis;
  };

  @modelAction
  setUserCreatedInvoicePayRequest = (payRequest: string | null) => {
    console.log("setting user created invoice pay request to", payRequest);
    this.userCreatedInvoicePayRequest = payRequest;
  };

  @modelAction
  setOpenModal = (modal: BobModalType | null) => {
    // Only log modal changes when they actually change
    if (this.openModal !== modal) {
      // console.log("[PAYMENT-FLOW] 🎭 Modal:", modal);
    }
    if (modal === null) {
      this.areWeWaitingOnRails = false;
    }
    this.openModal = modal;
  };

  @modelAction
  updateBalance = (balance: number, in_flight_satoshis: number) => {
    // Only log balance changes during active payments
    if (this.selectedPayAttempt) {
      // console.log(
      //   "[PAYMENT-FLOW] 💰 Balance:",
      //   this.safeBalanceInSatoshis,
      //   "→",
      //   balance
      // );
    }
    this.safeBalanceInSatoshis = balance;
    this.inFlightSatoshis = in_flight_satoshis;
  };

  @modelAction
  setOpenPage = (page: BobPageType) => {
    if (
      this.openPage === BOB_PAGES.SEND &&
      page !== BOB_PAGES.SEND &&
      this.selectedPayAttempt?.shouldResetWhenLeavingSendBeforeAmount
    ) {
      this.refreshPayAttempt();
    }

    // Only log navigation changes, not stack traces (reduces console spam)
    if (this.openPage !== page) {
      console.log("Page nav:", this.openPage, "→", page);
    }
    this.openPage = page;
  };

  @computed
  get workspace() {
    return findParent<BobsWorkspace>(
      this,
      (parentNode) => parentNode instanceof BobsWorkspace,
    );
  }

  @computed
  get lightningAddress() {
    return `${this.alias}@${this.workspace.baseDomain}`;
  }
  @computed
  get isLikelyDefaultAddress(): boolean {
    const alias = this.lightningAddress.split("@")[0];
    const dashCount = (alias.match(/-/g) || []).length;
    return dashCount >= 2;
  }

  @modelAction
  update = (bobData) => {
    // console.log("updating bob with data", bobData.alias, bobData);
    this.uuid = bobData.uuid;
    this.alias = bobData.alias;
    this.currencyCode = bobData.currency_code;
    this.showHistoryDays = bobData.show_history_days;
    this.userProposedAlias = bobData.alias; // Initialize userProposedAlias with current alias
    this.userProvidedNostrPublicKey = bobData.user_provided_nostr_public_key;
    // this.nostrPublicKey = bobData.nostr_public_key;
    // this.userFacingName = bobData.user_facing_name;
    this.safeBalanceInSatoshis = bobData.safe_balance_in_satoshis;
    this.pendingOnchainBalanceInSatoshis =
      bobData.pending_onchain_balance_in_satoshis;
    this.inFlightSatoshis = bobData.in_flight_satoshis;
    this.updatePayAttempts(bobData.pay_attempts);
    this.setOurInvoices(bobData.invoices);
    this.setHoldInvoices(bobData.hold_invoices || []);
    this.setPlatformCredits(bobData.platform_credits);
    this.setBobGifts(bobData.bob_gifts || []);
    this.setOurOnchainAddresses(bobData.onchain_addresses || []);
    this.setOurOnchainInboundTransactions(
      bobData.onchain_inbound_transactions || [],
    );
    this.setOurOnchainOutboundTransactions(
      bobData.onchain_outbound_transactions || [],
    );
    this.setOurStablePayments(bobData.stable_payments || []);
    this.setSponsorships(bobData.sponsorship || null);
    // console.log(
    //   "now have these onchain inbound transactions",
    //   this.ourOnchainInboundTransactions.data
    // );
  };

  @modelAction
  updatePayAttempts = (
    payAttemptsData: BobPayattemptFromServer[],
    isPageLoad = true,
  ) => {
    payAttemptsData.forEach((srv, index) => {
      const local = this.payAttempts.find((pa) => pa.uuid === srv.uuid);
      // const decoded = decode(srv.pay_request);
      // const summarizedInvoice = frozen(summariseBolt11Invoice(decoded));
      if (index == 0 && !isPageLoad) {
        handleFirstPayattemptFromServer(srv, this);
      }
      if (local) {
        /* update existing */
        local.payRequest = srv.pay_request;
        local.setStatus(srv.attempt_status as PayAttemptStatus);
        local.payAmountSatoshis = srv.satoshis_amount;
        local.maxRoutingFeeSatoshis = srv.max_routing_fee_satoshis;
        local.platformFeeSatoshis = srv.platform_fee_satoshis;
        local.routingFeesPaidSatoshis = srv.routing_fees_paid_satoshis;
        local.setCreatedAtTimestamp(srv.created_at_timestamp);
        local.paymentFailureErrorMessage = srv.payment_failure_error_message;
        local.userProposedLnurlPaymentComment = srv.comment;
        local.currencySnapshot = frozen(srv.currency_snapshot);
        local.finalLightningAddress = srv.lightning_address
          ? srv.lightning_address.trim()
          : undefined;
        local.zapName = srv.zap_name;
        local.zapPictureUrl = srv.zap_picture_url;
      } else {
        /* create new */
        const pa = new PayAttempt({
          uuid: srv.uuid,
          payRequest: srv.pay_request,
          payAttemptStatus: srv.attempt_status as PayAttemptStatus,
          payAmountSatoshis: srv.satoshis_amount,
          maxRoutingFeeSatoshis: srv.max_routing_fee_satoshis,
          platformFeeSatoshis: srv.platform_fee_satoshis,
          routingFeesPaidSatoshis: srv.routing_fees_paid_satoshis,
          createdAtTimestamp: srv.created_at_timestamp,
          paymentFailureErrorMessage: srv.payment_failure_error_message,
          userProposedLnurlPaymentComment: srv.comment,
          currencySnapshot: frozen(srv.currency_snapshot),
          finalLightningAddress: srv.lightning_address
            ? srv.lightning_address.trim()
            : undefined,
          // summarizedInvoice: summarizedInvoice,
          zapName: srv.zap_name,
          zapPictureUrl: srv.zap_picture_url,
        });
        this.payAttempts.push(pa);
      }
    });
  };

  @modelFlow
  makeNewOnchainAddress = _async(function* (this: Bob) {
    this.setAreWeWaitingOnRails(true);
    const message = {
      bobUuid: this.uuid,
    };
    sendToBobController(message, "/bob_life/make_new_onchain_address")
      .then((response) => {
        this.setAreWeWaitingOnRails(false);
        if (response.success == false) {
          notifications.show({
            title: "Error",
            message: response.message,
            color: "red",
          });
          return;
        }
        console.log("new onchain address created", response);
        this.pushInNewOnchainAddress(
          response.onchainAddress as OurOnchainAddress,
        );
        this.workspace.makeEvent(
          "Address Created",
          "A new on-chain Bitcoin address has been generated.",
          "onchain-address-created",
          response.onchainAddress.address,
        );
      })
      .catch((error) => {
        this.setAreWeWaitingOnRails(false);
        console.log(error);
        Sentry.captureException(error, {
          tags: {
            class: "Bob",
            function: "makeNewOnchainAddress",
            action: "onchain_address_creation",
          },
          contexts: {
            bob_details: {
              bobUuid: this.uuid,
              areWeWaitingOnRails: this.areWeWaitingOnRails,
            },
          },
        });
      });
  });

  @modelFlow
  createInvoice = _async(function* (
    this: Bob,
    amountInSatoshis: number,
    invoiceDescription: string,
  ) {
    this.setAreWeWaitingOnRails(true);
    const message = {
      bobUuid: this.uuid,
      amountInSatoshis,
      invoiceDescription,
    };
    sendToBobController(message, "/bob_life/create_invoice")
      .then((response) => {
        if (response.success == false) {
          notifications.show({
            title: "Error",
            message: response.message,
            color: "red",
          });
          return;
        }
        this.setUserCreatedInvoicePayRequest(response.pay_request);
        this.setAreWeWaitingOnRails(false);
        this.workspace.makeEvent(
          "Invoice Created",
          `An invoice in the amount of ${amountInSatoshis.toLocaleString()} satoshis has been created.`,
          "invoice-created",
          response.pay_request,
        );
        this.setOurInvoices(response.invoices);
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error, {
          tags: {
            class: "Bob",
            function: "createInvoice",
            action: "invoice_creation",
          },
          contexts: {
            invoice_details: {
              bobUuid: this.uuid,
              amountInSatoshis,
              invoiceDescription,
              areWeWaitingOnRails: this.areWeWaitingOnRails,
            },
          },
        });
      });
  });

  @computed
  get spreadsheetLines() {
    return makeSpreadsheetLines(this);
  }

  @computed
  get transactionsList() {
    return makeTransactionListsLines(
      this,
      this.transactionFilterType as TransactionFilterType,
    );
  }

  @computed
  get latestExchangeRate() {
    return this.workspace.exchangeRates.data[this.currencyCode];
  }

  @modelAction
  formatFiat = (satoshis: number, isNegative = false) => {
    return formatFiat({
      satoshis,
      currency: this.currencyCode,
      currencySnapshot: this.workspace.exchangeRates.data,
      isNegative: isNegative,
    });
  };
  @computed
  get transactionViewData() {
    return getTransactionData(this);
  }

  @computed
  get recentlyPaidLightningAddresses(): string[] {
    const uniq: string[] = [];

    this.payAttempts
      .filter(
        (pa) =>
          pa.payAttemptStatus === "has_succeeded" &&
          pa.finalLightningAddress &&
          pa.finalLightningAddress.trim().length > 0,
      )
      .sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp)
      .forEach((pa) => {
        const addr = pa.finalLightningAddress!.trim();
        if (!uniq.includes(addr)) {
          uniq.push(addr);
        }
      });

    return uniq.slice(0, 10);
  }
}

export interface BobInterface {
  Bob: Bob;
}

export const BobRef = rootRef<Bob>("exrails/BobRef", {
  onResolvedValueChange(ref, newThing, oldThing) {
    if (newThing && !oldThing) {
      detach(ref);
    }
  },
});
